import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './EmptyState.module.css';

interface EmptyStateProps {
  /** Custom title - overrides default translation */
  title?: string;
  /** Custom description - overrides default translation */
  description?: string;
  /** Button text - overrides default translation */
  buttonText?: string;
  /** Callback when action button is clicked */
  onAction?: () => void;
  /** Hide action button */
  hideAction?: boolean;
  /** Custom icon component */
  icon?: React.ReactNode;
}

export const EmptyState = ({ title, description, buttonText, onAction, hideAction = false, icon }: EmptyStateProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Decorative background elements */}
        <div className={styles.bgDecoration}>
          <div className={styles.bgCircle1} />
          <div className={styles.bgCircle2} />
          <div className={styles.bgCircle3} />
        </div>

        {/* Icon */}
        <div className={styles.iconWrapper}>{icon || <DefaultEmptyIcon />}</div>

        {/* Text content */}
        <div className={styles.textContent}>
          <h3 className={styles.title}>{title || t('emptyState.title')}</h3>
          <p className={styles.description}>{description || t('emptyState.description')}</p>
        </div>

        {/* Action button */}
        {!hideAction && onAction && (
          <Button type='primary' icon={<PlusOutlined />} onClick={onAction} className={styles.actionButton}>
            {buttonText || t('emptyState.addButton')}
          </Button>
        )}
      </div>
    </div>
  );
};

/** Default empty state illustration */
const DefaultEmptyIcon = () => (
  <svg width='120' height='120' viewBox='0 0 120 120' fill='none' xmlns='http://www.w3.org/2000/svg'>
    {/* Folder base */}
    <rect x='20' y='40' width='80' height='55' rx='8' className='emptyIcon-folder' />
    {/* Folder tab */}
    <path
      d='M20 48C20 43.5817 23.5817 40 28 40H45L52 32H92C96.4183 32 100 35.5817 100 40V40H20V48Z'
      className='emptyIcon-folderTab'
    />
    {/* Document 1 */}
    <rect x='35' y='52' width='30' height='38' rx='4' className='emptyIcon-doc' />
    {/* Document lines */}
    <rect x='41' y='60' width='18' height='3' rx='1.5' className='emptyIcon-line' />
    <rect x='41' y='67' width='14' height='3' rx='1.5' className='emptyIcon-line' />
    <rect x='41' y='74' width='16' height='3' rx='1.5' className='emptyIcon-line' />
    {/* Document 2 (behind) */}
    <rect x='55' y='48' width='30' height='38' rx='4' className='emptyIcon-docBehind' />
    {/* Plus circle */}
    <circle cx='85' cy='85' r='16' className='emptyIcon-plusCircle' />
    <rect x='79' y='83' width='12' height='4' rx='2' className='emptyIcon-plus' />
    <rect x='83' y='79' width='4' height='12' rx='2' className='emptyIcon-plus' />
  </svg>
);
